<script setup lang="ts">
import UIBreadcrumbs from '@/shared/ui/Breadcrumbs/index';
import type { IBreadcrumbs } from '@/shared/ui/Breadcrumbs/types';

const { $deliveryService } = useNuxtApp();
const { t } = useI18n();
const localePath = useLocalePath();

const breadcrumbs: Ref<IBreadcrumbs[]> = ref([
  { id: 1, text: t('main'), link: localePath('/') },
  { id: 2, text: t('pickupPoints'), link: '' },
]);

const { data: pickupPointsData } = await useAsyncData('pickup-points', async () => {
  const pickupPoints = await $deliveryService.getPickUpPoints();
  return pickupPoints.data;
});
</script>

<template>
  <div class="pickup-points">
    <div class="pickup-points__breadcrumbs">
      <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <section class="pickup-points__section">
      <h1 class="pickup-points__title">
        {{ t('pickupPoints') }}
      </h1>
      <ul class="pickup-points__list">
        <li
          class="pickup-points__item"
          v-for="pickupPoint in pickupPointsData"
          :key="pickupPoint.id"
        >
          <div class="pickup-points__content">
            <a
              :href="`https://yandex.uz/maps/-/CHXbIQND`"
              target="_blank"
              class="pickup-points__link _address"
              :aria-label="t('openInYandexMaps')"
            >
              {{ pickupPoint?.address }}
            </a>
            <div class="pickup-points__time">
              <div class="pickup-points__time-item">{{ pickupPoint?.work_time }}</div>
              <div class="pickup-points__time-item">
                *{{ t('pickupPointWorkingTime') }}
              </div>
            </div>
            <div class="pickup-points__phone">
              <div class="pickup-points__phone-title">{{ t('contactsPhones') }}</div>
              <div class="pickup-points__phone-item">
                <a
                  :href="`tel:${pickupPoint?.phone_number}`"
                  class="pickup-points__link _phone"
                  :aria-label="t('phoneNumber')"
                >
                  {{ pickupPoint?.phone_number }}
                </a>
                {{ t('arina') }}
              </div>
            </div>
          </div>
          <div class="pickup-points__map">
            <a
              href="https://yandex.uz/maps/-/CHXbIQND"
              target="_blank"
              :aria-label="t('openInYandexMaps')"
            >
              <NuxtImg
                src="/images/map.webp"
                :alt="t('pickupPoints')"
                class="pickup-points__map"
              />
            </a>
          </div>
        </li>
      </ul>
    </section>
  </div>
</template>

<style lang="less">
.pickup-points {
  .container-d();
  .flex-column(@size-x10);

  padding-top: @size-x8;
  padding-bottom: 72px;

  &__title {
    .h1();
  }

  &__section {
    .flex-column(@size-x8);
  }

  &__content {
    .flex-column(@size-x6);

    max-width: 525px;
  }

  &__list {
    .flex-column(@size-x6);
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: @size-x6;
    background-color: @color-athens-gray;
    padding: @size-x6 @size-x8;
    border-radius: @size-x6;
  }

  &__phone-title {
    font-size: @size-x5;
    font-weight: 600;
    line-height: 100%;
  }

  &__phone-item {
    font-size: 18px;
    font-weight: 500;
    line-height: 125%;
  }

  &__time {
    .flex-column(@size-x3);
  }

  &__time-item {
    font-size: @size-x4;
    font-weight: 400;
    line-height: 125%;
    color: @color-dove-gray-secondary;
  }

  &__link {
    .color-change-hover();

    &._address {
      font-size: @size-x5;
      font-weight: 600;
      line-height: 100%;
    }

    &._phone {
      color: @color-tango;
    }
  }

  &__phone {
    .flex-column(@size-x3);
  }

  &__map {
    display: block;
    width: 100%;
    max-width: 363px;
    height: 219px;
    object-fit: cover;
    border-radius: @size-x6;
  }

  @media @1024 {
    .container-m();

    gap: @size-x4;
    padding-top: @size-x6;
    padding-bottom: 48px;

    &__section {
      gap: @size-x6;
    }

    &__item {
      flex-direction: column;
      align-items: flex-start;
      padding: @size-x6 @size-x4;
    }

    &__content {
      max-width: 100%;
    }

    &__map {
      max-width: 100%;
      height: 180px;
    }

    &__phone-title {
      font-size: 18px;
    }

    &__link {
      &._address {
        font-size: 18px;
      }
    }

    &__time-item {
      font-size: 14px;
    }

    &__phone-item {
      font-size: @size-x4;
    }
  }
}
</style>
